// HTML cluster bubbles for the farm layer. MapLibre clusters the GeoJSON
// source; we render each cluster as a styled DOM marker (Plus Jakarta Sans,
// number inside) instead of a glyph-text layer — full typographic control and
// no font-PBF pipeline. Individual farms stay as a circle layer (their numbers
// live in the popup). Pattern: on every render, diff markers against the
// current cluster features so we add/move/remove only what changed.

import maplibregl from "maplibre-gl";

import { anchorCoord, clearAnchorCache } from "./clusterAnchor";

// The cluster bubble reflects the active analytics board: biomass / noise are
// metric ramps; ops / helse / compliance are "how many in the cluster" counts.
export type FarmMode = "biomass" | "noise" | "ops" | "helse" | "compliance";

// ---- colour ramps (JS side, for the HTML bubbles) -------------------------

type Stop = [number, string];

function hex(c: string): [number, number, number] {
  return [parseInt(c.slice(1, 3), 16), parseInt(c.slice(3, 5), 16), parseInt(c.slice(5, 7), 16)];
}
function rampColor(stops: Stop[], v: number): string {
  const first = stops[0]!;
  const last = stops[stops.length - 1]!;
  if (v <= first[0]) return first[1];
  if (v >= last[0]) return last[1];
  for (let i = 1; i < stops.length; i++) {
    const s1 = stops[i]!;
    if (v <= s1[0]) {
      const s0 = stops[i - 1]!;
      const t = (v - s0[0]) / (s1[0] - s0[0]);
      const a = hex(s0[1]);
      const b = hex(s1[1]);
      const r = Math.round(a[0] + (b[0] - a[0]) * t);
      const g = Math.round(a[1] + (b[1] - a[1]) * t);
      const bl = Math.round(a[2] + (b[2] - a[2]) * t);
      return `rgb(${r},${g},${bl})`;
    }
  }
  return last[1];
}

// Biomass intensity (tonnes per farm) — teal → green → gold → coral.
const BIOMASS_STOPS: Stop[] = [
  [0, "#2a9d8f"], [800, "#76b17d"], [1800, "#e9c46a"], [3200, "#f4a261"], [5000, "#e76f51"],
];
export const biomassColor = (t: number): string => rampColor(BIOMASS_STOPS, t);

// Noise (dB) — the existing functional ramp.
const NOISE_STOPS: Stop[] = [
  [115, "#2c7bb6"], [125, "#abd9e9"], [135, "#ffffbf"], [145, "#fdae61"], [155, "#d7191c"],
];
export const noiseColor = (db: number): string => rampColor(NOISE_STOPS, db);

// Lens count ramps (n sites in a cluster matching the lens) → warm hotspot tint.
const OPS_STOPS: Stop[] = [[0, "#3c5a4e"], [1, "#f6c177"], [4, "#f4a261"], [10, "#e76f51"]];
const LICE_STOPS: Stop[] = [[0, "#3c5a4e"], [1, "#f4a261"], [4, "#e76f51"], [10, "#d7191c"]];
const AVVIK_STOPS: Stop[] = [[0, "#3c5a4e"], [1, "#f08a7a"], [4, "#e63926"], [12, "#b3160a"]];

// ---- formatting -----------------------------------------------------------

export function fmtTonnes(t: number): string {
  if (t >= 10000) return `${Math.round(t / 1000)}k`;
  if (t >= 1000) return `${(t / 1000).toFixed(1)}k`;
  return `${Math.round(t / 10) * 10}`;
}

// ---- cluster aggregate read ----------------------------------------------

type ClusterAgg = {
  count: number;
  value: number;
  color: string;
  label: string;
  unit: string;
  dim: boolean;
};

function aggregate(props: Record<string, unknown>, mode: FarmMode): ClusterAgg {
  const count = Number(props.point_count) || 0;
  if (mode === "biomass") {
    const sum = Number(props.bsum) || 0;
    const mean = count ? sum / count : 0;
    return { count, value: sum, color: biomassColor(mean), label: fmtTonnes(sum), unit: "t", dim: false };
  }
  if (mode === "noise") {
    const dbn = Number(props.dbn) || 0;
    const mean = dbn ? (Number(props.dbsum) || 0) / dbn : 0;
    return { count, value: count, color: noiseColor(mean), label: mean ? mean.toFixed(0) : "–", unit: "dB", dim: false };
  }
  // Lens count modes: colour + label encode how many sites in the cluster match.
  const key = mode === "ops" ? "op_n" : mode === "helse" ? "lice_n" : "avvik_n";
  const stops = mode === "ops" ? OPS_STOPS : mode === "helse" ? LICE_STOPS : AVVIK_STOPS;
  const unit = mode === "ops" ? "i drift" : mode === "helse" ? "lus" : "avvik";
  const n = Number(props[key]) || 0;
  return { count, value: n, color: rampColor(stops, n), label: String(n), unit, dim: n === 0 };
}

function diameter(agg: ClusterAgg, mode: FarmMode): number {
  // Size encodes the additive quantity: total tonnes (biomass) or farm count
  // (everything else). sqrt so area ~ value; clamped for legibility.
  const base = mode === "biomass" ? Math.sqrt(agg.value) / 4.2 : Math.sqrt(agg.count) * 7;
  return Math.max(34, Math.min(72, 30 + base));
}

function styleBubble(el: HTMLElement, agg: ClusterAgg, mode: FarmMode): void {
  const d = diameter(agg, mode);
  el.style.width = `${d}px`;
  el.style.height = `${d}px`;
  el.style.background = agg.color;
  el.style.opacity = agg.dim ? "0.5" : "1";
  el.style.fontSize = `${Math.max(11, Math.min(16, d / 4))}px`;
  el.innerHTML =
    `<span class="fb-val">${agg.label}</span>` +
    `<span class="fb-unit">${agg.unit} · ${agg.count}</span>`;
}

function makeBubble(
  map: maplibregl.Map,
  sourceId: string,
  clusterId: number,
  agg: ClusterAgg,
  mode: FarmMode,
  lngLat: [number, number],
): HTMLElement {
  const el = document.createElement("div");
  el.className = "farm-bubble";
  styleBubble(el, agg, mode);
  el.addEventListener("click", () => {
    const src = map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
    src?.getClusterExpansionZoom(clusterId).then((zoom) => {
      map.easeTo({ center: lngLat, zoom: zoom + 0.2, duration: 500 });
    });
  });
  return el;
}

// ---- marker lifecycle -----------------------------------------------------

let markers: Record<string, maplibregl.Marker> = {};
let onScreen: Record<string, maplibregl.Marker> = {};

/** Rebuild from scratch on a mode change (bubble contents differ). */
export function resetClusterMarkers(): void {
  clearAnchorCache("receivers");
  for (const id in onScreen) onScreen[id]?.remove();
  markers = {};
  onScreen = {};
}

/** Sync HTML bubbles to the source's current clusters. Call on render/moveend. */
export function updateClusterMarkers(map: maplibregl.Map, sourceId: string, mode: FarmMode): void {
  if (!map.isSourceLoaded(sourceId)) return;
  const next: Record<string, maplibregl.Marker> = {};
  const features = map.querySourceFeatures(sourceId);
  for (const f of features) {
    const props = f.properties as Record<string, unknown>;
    if (!props.cluster) continue;
    const id = String(props.cluster_id);
    const centroid = (f.geometry as GeoJSON.Point).coordinates as [number, number];
    // Anchor to a real farm in the cluster, not the centroid (which drifts onto
    // land / open water in fjord geography).
    const coords = anchorCoord(map, sourceId, Number(props.cluster_id), centroid);
    const agg = aggregate(props, mode);
    let marker = markers[id];
    if (!marker) {
      const el = makeBubble(map, sourceId, Number(props.cluster_id), agg, mode, coords);
      marker = markers[id] = new maplibregl.Marker({ element: el }).setLngLat(coords);
    } else {
      styleBubble(marker.getElement(), agg, mode);
      marker.setLngLat(coords); // reposition once the real anchor resolves
    }
    next[id] = marker;
    if (!onScreen[id]) marker.addTo(map);
  }
  for (const id in onScreen) if (!next[id]) onScreen[id]?.remove();
  onScreen = next;
}
